export interface NationalProgram {
  id: string;
  programType: 'va_federal' | 'national_resource';
  title: string;
  category: 'education' | 'healthcare' | 'housing' | 'financial' | 'recreation' | 'employment' | 'mental_health' | 'emergency';
  description: string;
  eligibility: string[];
  applicationProcess: string;
  contactInfo: string;
  website: string;
  lastUpdated: string;
  specialNotes?: string;
  pAndTRelevant?: boolean;
  smcRelevant?: boolean;
  location?: string;
}

export const nationalProgramsData: NationalProgram[] = [
  // VA/Federal Benefits
  {
    id: 'va-001',
    programType: 'va_federal',
    title: 'VA Disability Compensation',
    category: 'financial',
    description: 'Monthly tax-free payments for veterans with service-connected disabilities, with additional amounts for dependents and Special Monthly Compensation (SMC) for specific circumstances.',
    eligibility: [
      'Must have service-connected disability rating from VA',
      'Must have been discharged under conditions other than dishonorable',
      'Additional compensation available for dependents at 30% or higher',
      'SMC available for specific severe disabilities or loss of use'
    ],
    applicationProcess: 'File VA Form 21-526EZ online at VA.gov, through VA regional office, or with accredited VSO assistance.',
    contactInfo: 'VA Benefits Hotline: 1-800-827-1000',
    website: 'https://www.va.gov/disability/',
    lastUpdated: 'Jan 2026',
    pAndTRelevant: true,
    smcRelevant: true,
    specialNotes: 'P&T (Permanent and Total) status provides additional benefits eligibility. SMC rates apply for severe disabilities.',
    location: 'National'
  },
  {
    id: 'va-002',
    programType: 'va_federal',
    title: 'Post-9/11 GI Bill',
    category: 'education',
    description: 'Education benefits covering tuition, housing allowance, and books for eligible veterans and their dependents.',
    eligibility: [
      'Must have served at least 90 days on active duty after September 10, 2001',
      'Must have been honorably discharged',
      'Benefit amount varies based on length of service',
      'Can be transferred to spouse or children if criteria met'
    ],
    applicationProcess: 'Apply online at VA.gov with Certificate of Eligibility. School must be VA-approved.',
    contactInfo: 'Education Call Center: 1-888-442-4551',
    website: 'https://www.va.gov/education/about-gi-bill-benefits/post-9-11/',
    lastUpdated: 'Dec 2025',
    specialNotes: 'Monthly housing allowance based on ZIP code of school. Yellow Ribbon Program may cover additional costs.',
    location: 'National'
  },
  {
    id: 'va-003',
    programType: 'va_federal',
    title: 'VA Healthcare',
    category: 'healthcare',
    description: 'Comprehensive healthcare services through VA medical centers and clinics nationwide.',
    eligibility: [
      'Must be veteran with qualifying service',
      'Must be enrolled in VA healthcare system',
      'Priority groups based on service-connected status and income',
      'Free care for service-connected conditions'
    ],
    applicationProcess: 'Apply using VA Form 10-10EZ online, by phone, mail, or in person at VA facility.',
    contactInfo: 'VA Healthcare: 1-877-222-8387',
    website: 'https://www.va.gov/health-care/',
    lastUpdated: 'Jan 2026',
    pAndTRelevant: true,
    specialNotes: '50% or higher service-connected veterans receive free healthcare. P&T status provides priority enrollment.',
    location: 'National'
  },
  {
    id: 'va-004',
    programType: 'va_federal',
    title: 'VA Home Loan Guaranty',
    category: 'housing',
    description: 'VA-backed home loans with no down payment, no PMI, and competitive rates for eligible veterans.',
    eligibility: [
      'Must meet minimum service requirements',
      'Must have satisfactory credit and income',
      'Certificate of Eligibility required',
      'Can be used multiple times with restored entitlement'
    ],
    applicationProcess: 'Obtain Certificate of Eligibility from VA, then work with VA-approved lender.',
    contactInfo: 'VA Home Loan Helpline: 1-877-827-3702',
    website: 'https://www.va.gov/housing-assistance/home-loans/',
    lastUpdated: 'Dec 2025',
    specialNotes: 'Adapted housing grants available for veterans with certain service-connected disabilities.',
    location: 'National'
  },
  {
    id: 'va-005',
    programType: 'va_federal',
    title: 'Vocational Rehabilitation & Employment (VR&E)',
    category: 'employment',
    description: 'Services and support to help veterans with service-connected disabilities prepare for, find, and maintain suitable employment.',
    eligibility: [
      'Must have VA service-connected disability rating of at least 10%',
      'Must have employment handicap',
      'Available for 12 years from notification of disability rating'
    ],
    applicationProcess: 'Apply using VA Form 28-1900 online or with VR&E counselor.',
    contactInfo: 'VR&E: 1-800-827-1000',
    website: 'https://www.va.gov/careers-employment/vocational-rehabilitation/',
    lastUpdated: 'Nov 2025',
    specialNotes: 'Provides training, education, job coaching, and employment assistance.',
    location: 'National'
  },

  // National Resources
  {
    id: 'national-001',
    programType: 'national_resource',
    title: 'Veterans Crisis Line',
    category: 'mental_health',
    description: '24/7 confidential crisis support for veterans and their families, including phone, text, and online chat.',
    eligibility: [
      'Available to all veterans, service members, and their families',
      'No enrollment or registration required',
      'Completely confidential'
    ],
    applicationProcess: 'Call, text, or chat immediately - no application needed.',
    contactInfo: 'Call 988, then press 1 | Text 838255 | Chat at VeteransCrisisLine.net',
    website: 'https://www.veteranscrisisline.net/',
    lastUpdated: 'Jan 2026',
    specialNotes: 'Connects to trained VA responders. Support for deaf and hard of hearing available.',
    location: 'National - 24/7 Service'
  },
  {
    id: 'national-002',
    programType: 'national_resource',
    title: 'Wounded Warrior Project',
    category: 'mental_health',
    description: 'Comprehensive programs and services for wounded veterans including mental health support, career counseling, and benefits assistance.',
    eligibility: [
      'Veterans injured on or after September 11, 2001',
      'Service members with physical or mental injuries, illnesses, or wounds',
      'All services completely free'
    ],
    applicationProcess: 'Register online or call to access programs and services.',
    contactInfo: '1-888-997-2586',
    website: 'https://www.woundedwarriorproject.org/',
    lastUpdated: 'Dec 2025',
    specialNotes: 'Offers peer support groups, benefits assistance, career services, and mental health programs.',
    location: 'National with local chapters'
  },
  {
    id: 'national-003',
    programType: 'national_resource',
    title: 'PGA HOPE (Helping Our Patriots Everywhere)',
    category: 'recreation',
    description: 'Free golf instruction and community for veterans to enhance physical, mental, social, and emotional well-being.',
    eligibility: [
      'All veterans and active duty military',
      'All skill levels welcome - no golf experience necessary',
      'Programs adapted for disabled veterans'
    ],
    applicationProcess: 'Find local program through PGA HOPE website and register for sessions.',
    contactInfo: 'Contact local PGA section',
    website: 'https://www.pgahope.com/',
    lastUpdated: 'Nov 2025',
    specialNotes: 'Includes adaptive equipment and instruction for veterans with disabilities. Multiple locations nationwide.',
    location: 'National - Find local chapter on website'
  },
  {
    id: 'national-004',
    programType: 'national_resource',
    title: 'Patriot Service Dogs (Emotional Support Animals)',
    category: 'mental_health',
    description: 'Assistance and emotional support dogs for veterans with PTSD, TBI, and other service-connected conditions.',
    eligibility: [
      'Veterans with PTSD, TBI, or other service-connected mental health conditions',
      'Must complete application and evaluation process',
      'Training and dog provided at no cost'
    ],
    applicationProcess: 'Submit application online. Evaluation and training matching process follows.',
    contactInfo: 'Varies by organization',
    website: 'Multiple organizations - search "veteran service dogs" + your state',
    lastUpdated: 'Dec 2025',
    specialNotes: 'Organizations like K9s For Warriors, Canine Companions, and local programs available. Check VA.gov for approved providers.',
    location: 'National - Multiple organizations'
  },
  {
    id: 'national-005',
    programType: 'national_resource',
    title: 'Team Rubicon',
    category: 'employment',
    description: 'Veteran-led disaster response organization providing meaningful service opportunities and skills development.',
    eligibility: [
      'All veterans and military-affiliated individuals',
      'No specific experience required',
      'Training provided'
    ],
    applicationProcess: 'Register online and complete orientation. Deploy for disaster response operations.',
    contactInfo: '1-888-748-2466',
    website: 'https://teamrubiconusa.org/',
    lastUpdated: 'Nov 2025',
    specialNotes: 'Provides sense of purpose, camaraderie, and skills development through disaster response service.',
    location: 'National with local operations'
  },
  {
    id: 'national-006',
    programType: 'national_resource',
    title: 'DAV (Disabled American Veterans)',
    category: 'financial',
    description: 'Free claims assistance and representation for veterans seeking VA benefits and appeals.',
    eligibility: [
      'All veterans seeking VA benefits',
      'Particularly focused on disabled veterans',
      'Services completely free'
    ],
    applicationProcess: 'Contact local DAV chapter or national service office for assistance with claims.',
    contactInfo: '1-877-426-2838',
    website: 'https://www.dav.org/',
    lastUpdated: 'Jan 2026',
    specialNotes: 'Accredited service officers assist with VA claims at no charge. Also provides transportation to VA appointments.',
    location: 'National - Find local chapter on website'
  },
  {
    id: 'national-007',
    programType: 'national_resource',
    title: 'The Mission Continues',
    category: 'employment',
    description: 'Veteran-led community service programs connecting veterans to fellowship opportunities and community impact projects.',
    eligibility: [
      'All veterans',
      'Fellowship programs available',
      'Community service opportunities nationwide'
    ],
    applicationProcess: 'Apply for fellowship programs online or join local service platoon.',
    contactInfo: '1-314-588-8805',
    website: 'https://missioncontinues.org/',
    lastUpdated: 'Dec 2025',
    specialNotes: 'Six-month fellowship provides leadership development and community service experience.',
    location: 'National with local service platoons'
  },
  {
    id: 'national-008',
    programType: 'national_resource',
    title: 'Military OneSource',
    category: 'mental_health',
    description: 'Free 24/7 counseling, specialty consultations, and resources for service members and veterans.',
    eligibility: [
      'Active duty, Guard, Reserve, and veterans',
      'Family members also eligible',
      'Available 365 days a year'
    ],
    applicationProcess: 'Call, chat online, or access resources through website.',
    contactInfo: '1-800-342-9647',
    website: 'https://www.militaryonesource.mil/',
    lastUpdated: 'Jan 2026',
    specialNotes: 'Provides non-medical counseling, financial consulting, education support, and specialty consultations.',
    location: 'National - 24/7 Service'
  },
  {
    id: 'national-009',
    programType: 'national_resource',
    title: 'National Veterans Foundation',
    category: 'emergency',
    description: 'Crisis intervention and resource referrals for veterans in need of immediate assistance.',
    eligibility: [
      'All veterans',
      'Families of veterans',
      'Service available 24/7'
    ],
    applicationProcess: 'Call hotline for immediate assistance and resource referrals.',
    contactInfo: '1-888-777-4443',
    website: 'https://nvf.org/',
    lastUpdated: 'Dec 2025',
    specialNotes: 'Lifeline for Vets hotline provides crisis support and connects veterans to local resources.',
    location: 'National - 24/7 Hotline'
  },
  {
    id: 'national-010',
    programType: 'national_resource',
    title: 'Hire Heroes USA',
    category: 'employment',
    description: 'Free job search assistance, resume writing, interview coaching, and career mentoring for veterans.',
    eligibility: [
      'All U.S. military members, veterans, and military spouses',
      'All services completely free',
      'Remote and in-person services available'
    ],
    applicationProcess: 'Register online to access services and connect with career coach.',
    contactInfo: '1-866-915-4376',
    website: 'https://www.hireheroesusa.org/',
    lastUpdated: 'Nov 2025',
    specialNotes: 'Personalized job search support with one-on-one coaching and workshops.',
    location: 'National - Virtual and in-person services'
  }
];

export const getProgramsByType = (programType: 'va_federal' | 'national_resource'): NationalProgram[] => {
  return nationalProgramsData.filter(program => program.programType === programType);
};

export const getProgramsByCategory = (category: string): NationalProgram[] => {
  return nationalProgramsData.filter(program => program.category === category);
};

export const getPAndTRelevantPrograms = (): NationalProgram[] => {
  return nationalProgramsData.filter(program => program.pAndTRelevant);
};

export const getSMCRelevantPrograms = (): NationalProgram[] => {
  return nationalProgramsData.filter(program => program.smcRelevant);
};